import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  downloadTestRunSpecExcel,
  formatSpecDate,
  formatSpecListMeta,
  useDeleteTestRunSpec,
  useTestRunSpecDetail,
  useTestRunSpecs,
  type TestRunSpecDetail,
  type TestRunSpecMethod,
  type TestRunSpecSummary,
} from '@/components/smartai/api/testRunSpecs';
import { cn } from '@/lib/utils';
import {
  testOptPlaceholderErrorClass,
  testOptTableMethodTypeClass,
  testOptTdClass,
  testOptTdNumClass,
  testOptThClass,
  testOptThNumClass,
  testRunSpecsClass,
  testRunSpecsColLinesClass,
  testRunSpecsColMethodClass,
  testRunSpecsColTcClass,
  testRunSpecsDetailActionsClass,
  testRunSpecsDetailErrorClass,
  testRunSpecsDetailScrollClass,
  testRunSpecsDetailSubtitleClass,
  testRunSpecsDetailTopClass,
  testRunSpecsDetailViewClass,
  testRunSpecsEmptyHintClass,
  testRunSpecsEmptyMainClass,
  testRunSpecsExtraTcListClass,
  testRunSpecsListClass,
  testRunSpecsListDateClass,
  testRunSpecsListDateActiveClass,
  testRunSpecsListItem,
  testRunSpecsListItemBodyClass,
  testRunSpecsListItemIndicatorActiveClass,
  testRunSpecsListItemIndicatorClass,
  testRunSpecsListItemIndicatorIdleClass,
  testRunSpecsListMetaClass,
  testRunSpecsListMetaActiveClass,
  testRunSpecsListNameClass,
  testRunSpecsListNameActiveClass,
  testRunSpecsLoadingClass,
  testRunSpecsMainClass,
  testRunSpecsMethodCellClass,
  testRunSpecsMethodNameClass,
  testRunSpecsNoTcClass,
  testRunSpecsSidebarClass,
  testRunSpecsSidebarHeadClass,
  testRunSpecsSourceClass,
  testRunSpecsSourceLegendClass,
  testRunSpecsStatClass,
  testRunSpecsStatLabelClass,
  testRunSpecsStatValue,
  testRunSpecsStatsClass,
  testRunSpecsTableClass,
  testRunSpecsTableEmptyClass,
  testRunSpecsTableHeadClass,
  testRunSpecsTablePanelClass,
  testRunSpecsTablePanelExtraClass,
  testRunSpecsTableRowGapClass,
  testRunSpecsTableWrapClass,
  testRunSpecsTcBadgesClass,
  testRunSpecsTcCellClass,
  testRunSpecsTcChipClass,
  testRunSpecsTcChipTopClass,
  testRunSpecsTcIdClass,
  testRunSpecsTcListClass,
  testRunSpecsTcNameClass,
  testRunSpecsTcScriptClass,
} from './test-optimization-layout';

type Props = {
  scanId: string;
  loading?: boolean;
  onCreateNew?: () => void;
};

type TestCaseSourceTone = 'selected' | 'dependency' | 'changed' | 'failed' | 'coverage';

type TestCaseSourceBadge = {
  id: string;
  label: string;
  tone: TestCaseSourceTone;
  tooltip?: string;
};

type MappedTestCase = {
  testCaseId: string;
  testCaseName: string;
  isFailed: boolean;
  sourceBadges: TestCaseSourceBadge[];
  testScriptPath?: string;
};

type MethodCoverageRow = {
  key: string;
  name: string;
  type: string;
  filePath: string;
  lineStart?: number;
  lineEnd?: number;
  priority: string;
  priorityLabel: string;
  testCases: MappedTestCase[];
  inclusionBadge?: TestCaseSourceBadge;
};

const SOURCE_BADGE_ORDER: TestCaseSourceTone[] = [
  'failed',
  'changed',
  'selected',
  'dependency',
  'coverage',
];

const SOURCE_TYPE_TO_BADGE: Record<string, TestCaseSourceBadge> = {
  selected: { id: 'selected', label: 'Selected', tone: 'selected' },
  selected_fan_in: { id: 'dependency', label: 'Dependency', tone: 'dependency' },
  selected_fan_out: { id: 'dependency', label: 'Dependency', tone: 'dependency' },
  covers_selected_method: {
    id: 'selected',
    label: 'Selected',
    tone: 'selected',
  },
  covers_selected_dependency: {
    id: 'dependency',
    label: 'Dependency',
    tone: 'dependency',
  },
  changed_script: { id: 'changed', label: 'Changed build', tone: 'changed' },
  covers_changed_script: {
    id: 'changed',
    label: 'Changed build',
    tone: 'changed',
  },
  failed: { id: 'failed', label: 'Failed', tone: 'failed' },
  covers_failed_method: { id: 'failed', label: 'Failed', tone: 'failed' },
};

const LEGACY_COVERAGE_BADGE: TestCaseSourceBadge = {
  id: 'coverage',
  label: 'Coverage',
  tone: 'coverage',
};

const FAN_SOURCE_TYPES = new Set(['selected_fan_in', 'selected_fan_out']);

function methodSourceTypes(method?: {
  sources?: { type?: string }[];
}): string[] {
  return (method?.sources ?? []).map(s => s.type).filter((t): t is string => Boolean(t));
}

function isUserSelectedMethod(method?: { sources?: { type?: string }[] }): boolean {
  return methodSourceTypes(method).includes('selected');
}

function isDependencyMethod(method?: { sources?: { type?: string }[] }): boolean {
  return methodSourceTypes(method).some(t => FAN_SOURCE_TYPES.has(t));
}

function methodInclusionBadge(
  method?: { sources?: { type?: string }[] },
): TestCaseSourceBadge | undefined {
  if (isUserSelectedMethod(method)) return SOURCE_TYPE_TO_BADGE.selected;
  if (isDependencyMethod(method)) return SOURCE_TYPE_TO_BADGE.selected_fan_in;
  return undefined;
}

/**
 * Tests covering fan-in/fan-out methods used to be stored as
 * ``covers_selected_method``. When viewing a dependency method row, show
 * Dependency instead of Selected.
 */
function sourceTypesForMethodRow(
  catalogTypes: string[],
  method?: { sources?: { type?: string }[] },
): string[] {
  if (!isDependencyMethod(method) || isUserSelectedMethod(method)) {
    return catalogTypes;
  }
  return catalogTypes.map(type =>
    type === 'covers_selected_method' ? 'covers_selected_dependency' : type,
  );
}

function isFailedTestCase(meta: {
  lastKnownStatus?: string;
  sources?: { type?: string }[];
}): boolean {
  const status = (meta.lastKnownStatus ?? '').trim().toUpperCase();
  if (status === 'FAILED' || status === 'FAIL') return true;
  return (meta.sources ?? []).some(s => s.type === 'failed');
}

function mergeSourceTypes(
  existing: string[],
  incoming?: { type?: string }[],
): string[] {
  const set = new Set(existing);
  for (const s of incoming ?? []) {
    if (s.type) set.add(s.type);
  }
  return [...set];
}

function buildSourceBadges(
  sourceTypes: string[],
  isFailed: boolean,
): TestCaseSourceBadge[] {
  const byId = new Map<string, TestCaseSourceBadge>();

  for (const type of sourceTypes) {
    const badge = SOURCE_TYPE_TO_BADGE[type];
    if (badge) byId.set(badge.id, badge);
  }

  if (byId.size === 0) {
    if (isFailed) byId.set('failed', SOURCE_TYPE_TO_BADGE.failed!);
    else byId.set('coverage', LEGACY_COVERAGE_BADGE);
  } else if (isFailed) {
    byId.set('failed', SOURCE_TYPE_TO_BADGE.failed!);
  }

  return SOURCE_BADGE_ORDER.flatMap(tone => {
    const match = [...byId.values()].find(b => b.tone === tone);
    return match ? [match] : [];
  });
}

type CatalogEntry = {
  testCaseId: string;
  testCaseName: string;
  isFailed: boolean;
  sourceTypes: string[];
};

function upsertCatalogEntry(
  map: Map<string, CatalogEntry>,
  entry: {
    testCaseId: string;
    testCaseName?: string;
    lastKnownStatus?: string;
    sources?: { type?: string }[];
  },
) {
  const id = entry.testCaseId;
  if (!id) return;
  const existing = map.get(id);
  const sourceTypes = mergeSourceTypes(
    existing?.sourceTypes ?? [],
    entry.sources,
  );
  const failed = isFailedTestCase(entry) || Boolean(existing?.isFailed);
  map.set(id, {
    testCaseId: id,
    testCaseName: entry.testCaseName || existing?.testCaseName || id,
    isFailed: failed,
    sourceTypes,
  });
}

function buildTestCaseCatalog(spec: TestRunSpecDetail): Map<string, CatalogEntry> {
  const map = new Map<string, CatalogEntry>();

  for (const tc of spec.testCases ?? []) {
    upsertCatalogEntry(map, tc);
  }

  for (const group of spec.priorities ?? []) {
    for (const batch of group.batches ?? []) {
      if (batch.testCaseId) {
        upsertCatalogEntry(map, batch);
      }
    }
  }

  return map;
}

function resolveTestCaseDisplayName(
  testCaseId: string,
  catalog: Map<string, CatalogEntry>,
  preferredName?: string,
): string {
  const preferred = preferredName?.trim();
  if (preferred && preferred !== testCaseId) return preferred;

  const fromCatalog = catalog.get(testCaseId)?.testCaseName?.trim();
  if (fromCatalog && fromCatalog !== testCaseId) return fromCatalog;

  return preferred || fromCatalog || testCaseId;
}

function toMappedTestCase(
  tc: { testCaseId: string; testCaseName?: string },
  catalog: Map<string, CatalogEntry>,
  method?: { sources?: { type?: string }[] },
): MappedTestCase {
  const fromCatalog = catalog.get(tc.testCaseId);
  const isFailed = fromCatalog?.isFailed ?? false;
  const sourceTypes = sourceTypesForMethodRow(
    fromCatalog?.sourceTypes ?? [],
    method,
  );
  const testCaseName = resolveTestCaseDisplayName(
    tc.testCaseId,
    catalog,
    tc.testCaseName,
  );
  return {
    testCaseId: tc.testCaseId,
    testCaseName,
    isFailed,
    sourceBadges: buildSourceBadges(sourceTypes, isFailed),
  };
}

function resolveMethodTestCases(
  method: TestRunSpecMethod,
  catalog: Map<string, CatalogEntry>,
  spec: TestRunSpecDetail,
): MappedTestCase[] {
  if (method.coveredByTests?.length) {
    return method.coveredByTests.map(tc =>
      toMappedTestCase(
        { testCaseId: tc.testCaseId, testCaseName: tc.testCaseName },
        catalog,
        method,
      ),
    );
  }

  const linked = method.linkedTestCaseIds ?? [];
  if (linked.length) {
    return linked.map(id => toMappedTestCase({ testCaseId: id }, catalog, method));
  }

  const found = new Map<string, MappedTestCase>();
  for (const group of spec.priorities ?? []) {
    for (const batch of group.batches ?? []) {
      const keys = batch.entityKeys ?? [];
      const names = batch.methodNames ?? [];
      const matches =
        keys.includes(method.entityKey) || names.includes(method.name);
      if (matches && batch.testCaseId) {
        found.set(
          batch.testCaseId,
          toMappedTestCase(
            {
              testCaseId: batch.testCaseId,
              testCaseName: batch.testCaseName || batch.testCaseId,
            },
            catalog,
            method,
          ),
        );
      }
    }
  }

  return [...found.values()];
}

function getAllSpecMethods(spec: TestRunSpecDetail): TestRunSpecMethod[] {
  if (spec.methods?.length) return spec.methods;

  const byKey = new Map<string, TestRunSpecMethod>();
  for (const m of spec.methodsWithoutTests ?? []) {
    byKey.set(m.entityKey, m);
  }

  for (const group of spec.priorities ?? []) {
    for (const batch of group.batches ?? []) {
      for (let i = 0; i < (batch.entityKeys?.length ?? 0); i++) {
        const ek = batch.entityKeys[i]!;
        const name = batch.methodNames[i] ?? ek;
        if (!byKey.has(ek)) {
          byKey.set(ek, {
            entityKey: ek,
            name,
            type: 'Method',
          });
        }
      }
    }
  }

  return [...byKey.values()];
}

function extractTestScriptPath(
  sources?: { testScriptPath?: string }[],
  fallback?: string,
): string | undefined {
  if (fallback?.trim()) return fallback.trim();
  for (const source of sources ?? []) {
    if (source.testScriptPath?.trim()) return source.testScriptPath.trim();
  }
  return undefined;
}

function hasChangedBuildSource(sourceTypes: string[]): boolean {
  return sourceTypes.some(
    type => type === 'changed_script' || type === 'covers_changed_script',
  );
}

function hasDirectFailedSource(sourceTypes: string[]): boolean {
  return sourceTypes.includes('failed');
}

function collectMappedTestCaseIds(rows: MethodCoverageRow[]): Set<string> {
  const ids = new Set<string>();
  for (const row of rows) {
    for (const tc of row.testCases) {
      ids.add(tc.testCaseId);
    }
  }
  return ids;
}

type SpecTestCaseRef = {
  testCaseId?: string;
  testCaseName?: string;
  sources?: { type?: string; testScriptPath?: string }[];
  testScriptPath?: string;
};

function forEachSpecTestCaseRef(
  spec: TestRunSpecDetail,
  visit: (entry: SpecTestCaseRef) => void,
): void {
  for (const tc of spec.testCases ?? []) {
    visit(tc);
  }
  for (const group of spec.priorities ?? []) {
    for (const batch of group.batches ?? []) {
      visit(batch);
    }
  }
}

function buildUnmappedTestCases(
  spec: TestRunSpecDetail,
  catalog: Map<string, CatalogEntry>,
  mappedIds: Set<string>,
  excludeIds: Set<string>,
  include: (sourceTypes: string[]) => boolean,
  enrich?: (mapped: MappedTestCase, entry: SpecTestCaseRef) => MappedTestCase,
): MappedTestCase[] {
  const unmapped: MappedTestCase[] = [];
  const seen = new Set<string>();

  forEachSpecTestCaseRef(spec, entry => {
    const testCaseId = entry.testCaseId?.trim();
    if (
      !testCaseId ||
      mappedIds.has(testCaseId) ||
      excludeIds.has(testCaseId) ||
      seen.has(testCaseId)
    ) {
      return;
    }

    const catalogEntry = catalog.get(testCaseId);
    const sourceTypes = catalogEntry?.sourceTypes ?? [];
    if (!include(sourceTypes)) return;

    seen.add(testCaseId);
    const mapped = toMappedTestCase(
      { testCaseId, testCaseName: entry.testCaseName || catalogEntry?.testCaseName },
      catalog,
    );
    unmapped.push(enrich ? enrich(mapped, entry) : mapped);
  });

  return unmapped.sort((a, b) => a.testCaseId.localeCompare(b.testCaseId));
}

function withTestScriptPath(
  mapped: MappedTestCase,
  entry: SpecTestCaseRef,
): MappedTestCase {
  return {
    ...mapped,
    testScriptPath: extractTestScriptPath(entry.sources, entry.testScriptPath),
  };
}

function buildSpecCoverageView(spec: TestRunSpecDetail): {
  rows: MethodCoverageRow[];
  changedBuildTestCases: MappedTestCase[];
  failedUnmappedTestCases: MappedTestCase[];
} {
  const catalog = buildTestCaseCatalog(spec);
  const priorityRank = (p: string) =>
    p === 'P1' ? 0 : p === 'P2' ? 1 : 2;

  const rows = getAllSpecMethods(spec)
    .map(method => ({
      key: method.entityKey || method.name,
      name: method.name,
      type: method.type ?? 'Method',
      filePath: method.filePath ?? '—',
      lineStart: method.lineStart,
      lineEnd: method.lineEnd,
      priority: method.priority ?? '—',
      priorityLabel: method.priorityLabel ?? '',
      testCases: resolveMethodTestCases(method, catalog, spec),
      inclusionBadge: methodInclusionBadge(method),
    }))
    .sort((a, b) => {
      const byPriority = priorityRank(a.priority) - priorityRank(b.priority);
      if (byPriority !== 0) return byPriority;
      return a.name.localeCompare(b.name);
    });

  const mappedIds = collectMappedTestCaseIds(rows);
  const changedBuildTestCases = buildUnmappedTestCases(
    spec,
    catalog,
    mappedIds,
    new Set(),
    hasChangedBuildSource,
    withTestScriptPath,
  );
  const failedUnmappedTestCases = buildUnmappedTestCases(
    spec,
    catalog,
    mappedIds,
    new Set(changedBuildTestCases.map(tc => tc.testCaseId)),
    hasDirectFailedSource,
    withTestScriptPath,
  );

  return { rows, changedBuildTestCases, failedUnmappedTestCases };
}

function sourceBadgeClass(tone: TestCaseSourceTone): string {
  return testRunSpecsSourceClass(tone);
}

const SOURCE_LEGEND: TestCaseSourceBadge[] = [
  {
    ...SOURCE_TYPE_TO_BADGE.selected!,
    tooltip: 'Test case covers a method you selected when saving the spec',
  },
  {
    ...SOURCE_TYPE_TO_BADGE.changed_script!,
    tooltip: 'Added from a test script modified in the latest build',
  },
  {
    ...SOURCE_TYPE_TO_BADGE.failed!,
    tooltip: 'Failed in the latest script build run',
  },
  {
    ...SOURCE_TYPE_TO_BADGE.selected_fan_in!,
    tooltip: 'Method reached through upstream or downstream dependencies',
  },
  {
    ...LEGACY_COVERAGE_BADGE,
    tooltip: 'Older spec — how this test case was included was not recorded',
  },
];

function TestCaseChip({ tc }: { tc: MappedTestCase }) {
  const showName = tc.testCaseName !== tc.testCaseId;

  return (
    <div className={testRunSpecsTcChipClass}>
      <div className={testRunSpecsTcChipTopClass}>
        <span className={testRunSpecsTcIdClass}>{tc.testCaseId}</span>
      </div>
      {showName && <span className={testRunSpecsTcNameClass}>{tc.testCaseName}</span>}
      <div className={testRunSpecsTcBadgesClass}>
        {tc.sourceBadges.map(badge => (
          <span
            key={`${tc.testCaseId}-${badge.id}`}
            className={sourceBadgeClass(badge.tone)}>
            {badge.label}
          </span>
        ))}
      </div>
      {tc.testScriptPath && (
        <span className={testRunSpecsTcScriptClass} title={tc.testScriptPath}>
          {tc.testScriptPath}
        </span>
      )}
    </div>
  );
}

const TestRunSpecsTab = ({ scanId, loading: parentLoading, onCreateNew }: Props) => {
  const { data: listData, isPending: listLoading, isError: listError } = useTestRunSpecs(scanId);
  const specs = listData?.specs ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!specs.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !specs.some(s => s.specId === selectedId)) {
      setSelectedId(specs[0]!.specId);
    }
  }, [specs, selectedId]);

  const {
    data: detailData,
    isError: detailError,
  } = useTestRunSpecDetail(selectedId, Boolean(selectedId));

  const deleteMutation = useDeleteTestRunSpec(scanId);
  const spec = detailData?.spec;
  const specReady = Boolean(spec && spec.specId === selectedId);

  const loading = parentLoading || listLoading;

  if (loading) {
    return (
      <div className={cn(testRunSpecsClass, testRunSpecsLoadingClass)}>
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      </div>
    );
  }

  if (listError) {
    return (
      <p className={testOptPlaceholderErrorClass}>
        Failed to load test run specs.
      </p>
    );
  }

  return (
    <div className={testRunSpecsClass}>
      <aside className={testRunSpecsSidebarClass}>
        <div className={testRunSpecsSidebarHeadClass}>
          <h2>Saved Specs</h2>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-8 shrink-0 gap-1 px-2.5 text-[0.8125rem] font-semibold"
            onClick={onCreateNew}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New
          </Button>
        </div>
        <div className={testRunSpecsListClass}>
          {specs.length === 0 ? (
            <p className={testRunSpecsEmptyHintClass}>
              No specs yet. Select methods on the Methods tab and save a Test Run Spec.
            </p>
          ) : (
            specs.map(item => (
              <SpecListItem
                key={item.specId}
                item={item}
                active={item.specId === selectedId}
                onSelect={() => setSelectedId(item.specId)}
              />
            ))
          )}
        </div>
      </aside>

      <main className={testRunSpecsMainClass}>
        {!selectedId ? (
          <div className={testRunSpecsEmptyMainClass}>
            <h3>Test Run Specs</h3>
            <p>
              Create a spec from the Methods tab, then review selected methods and mapped
              test cases here.
            </p>
            {specs.length === 0 && (
              <Button onClick={onCreateNew}>
                Go to Methods
              </Button>
            )}
          </div>
        ) : detailError ? (
          <div className={testRunSpecsEmptyMainClass}>
            <h3>Could not load spec</h3>
            <p className={testRunSpecsDetailErrorClass}>
              Failed to load this test run spec. Try selecting it again.
            </p>
          </div>
        ) : !specReady ? (
          <div className={testRunSpecsLoadingClass}>
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          </div>
        ) : specReady && spec ? (
          <SpecDetailView
            spec={spec}
            deleting={deleteMutation.isPending}
            onDelete={() => {
              if (!window.confirm(`Delete "${spec.name}"?`)) return;
              deleteMutation.mutate(spec.specId, {
                onSuccess: () => setSelectedId(null),
              });
            }}
            onDownload={() =>
              void downloadTestRunSpecExcel(
                spec.specId,
                `${spec.name.replace(/[^a-zA-Z0-9_-]+/g, '_') || 'test-run-spec'}.xlsx`,
              )
            }
          />
        ) : (
          <div className={testRunSpecsLoadingClass}>
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

function SpecListItem({
  item,
  active,
  onSelect,
}: {
  item: TestRunSpecSummary;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={testRunSpecsListItem(active)}
      aria-current={active ? 'true' : undefined}
      onClick={onSelect}>
      <span
        aria-hidden
        className={cn(
          testRunSpecsListItemIndicatorClass,
          active
            ? testRunSpecsListItemIndicatorActiveClass
            : testRunSpecsListItemIndicatorIdleClass,
        )}
      />
      <span className={testRunSpecsListItemBodyClass}>
        <span
          className={cn(
            testRunSpecsListNameClass,
            active && testRunSpecsListNameActiveClass,
          )}
          title={item.name}>
          {item.name}
        </span>
        <span
          className={cn(
            testRunSpecsListMetaClass,
            active && testRunSpecsListMetaActiveClass,
          )}
          title={formatSpecListMeta(item)}>
          {formatSpecListMeta(item)}
        </span>
        <time
          className={cn(
            testRunSpecsListDateClass,
            active && testRunSpecsListDateActiveClass,
          )}
          dateTime={item.createdAt}>
          {formatSpecDate(item.createdAt)}
        </time>
      </span>
    </button>
  );
}

function SpecDetailView({
  spec,
  deleting,
  onDelete,
  onDownload,
}: {
  spec: TestRunSpecDetail;
  deleting: boolean;
  onDelete: () => void;
  onDownload: () => void;
}) {
  const summary = spec.summary ?? {};
  const { rows, changedBuildTestCases, failedUnmappedTestCases } = useMemo(
    () => buildSpecCoverageView(spec),
    [spec],
  );
  const withTests = rows.filter(r => r.testCases.length > 0).length;
  const withoutTests = rows.length - withTests;
  const failedTestCount = useMemo(() => {
    const ids = new Set<string>();
    for (const row of rows) {
      for (const tc of row.testCases) {
        if (tc.isFailed) ids.add(tc.testCaseId);
      }
    }
    for (const tc of changedBuildTestCases) {
      if (tc.isFailed) ids.add(tc.testCaseId);
    }
    for (const tc of failedUnmappedTestCases) {
      ids.add(tc.testCaseId);
    }
    return ids.size;
  }, [rows, changedBuildTestCases, failedUnmappedTestCases]);

  return (
    <div className={testRunSpecsDetailViewClass}>
      <header className={testRunSpecsDetailTopClass}>
        <div>
          <h2>{spec.name}</h2>
          <p className={testRunSpecsDetailSubtitleClass}>
            Saved {formatSpecDate(spec.createdAt)} · {rows.length} methods · {withTests}{' '}
            mapped · {withoutTests} gaps
            {changedBuildTestCases.length > 0
              ? ` · ${changedBuildTestCases.length} changed build test case(s)`
              : ''}
            {failedUnmappedTestCases.length > 0
              ? ` · ${failedUnmappedTestCases.length} unmapped failed test case(s)`
              : ''}
            {failedTestCount > 0 ? ` · ${failedTestCount} failed test case(s)` : ''}
          </p>
        </div>
        <div className={testRunSpecsDetailActionsClass}>
          <Button variant="secondary" onClick={onDelete} disabled={deleting}>
            Delete
          </Button>
          <Button onClick={onDownload}>
            Download for Test Team
          </Button>
        </div>
      </header>

      <div className={testRunSpecsStatsClass}>
        <StatBox label="Methods" value={summary.methodCount ?? rows.length} />
        <StatBox
          label="Have Tests"
          value={summary.haveTestsCount ?? withTests}
          tone="success"
        />
        <StatBox
          label="No Tests"
          value={summary.noTestsCount ?? withoutTests}
          tone="danger"
        />
        <StatBox label="Tests to Run" value={summary.testsToRunCount ?? 0} />
      </div>

      <div className={testRunSpecsDetailScrollClass}>
      <div className={testRunSpecsTablePanelClass}>
        <div className={testRunSpecsTableHeadClass}>
          <h3>Methods &amp; mapped test cases</h3>
          <p>
            Each row is a method in this spec — the ones you picked, plus any
            callers/callees pulled in by the first checkbox. Badges show whether
            a method was selected or included as a dependency, and how each test
            case was added.
          </p>
          <div className={testRunSpecsSourceLegendClass} aria-label="Test case source legend">
            {SOURCE_LEGEND.map(item => (
              <span
                key={item.id}
                className={sourceBadgeClass(item.tone)}
                title={item.tooltip}>
                {item.label}
              </span>
            ))}
          </div>
        </div>

        {rows.length === 0 ? (
          <p className={testRunSpecsTableEmptyClass}>No methods in this spec.</p>
        ) : (
          <div className={testRunSpecsTableWrapClass}>
            <table className={testRunSpecsTableClass}>
              <thead>
                <tr>
                  <th className={cn(testOptThClass, testRunSpecsColMethodClass)}>Method</th>
                  <th className={cn(testOptThNumClass, testRunSpecsColLinesClass)}>Lines</th>
                  <th className={cn(testOptThClass, testRunSpecsColTcClass)}>
                    Mapped test case(s)
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={row.key}
                    className={
                      row.testCases.length === 0
                        ? testRunSpecsTableRowGapClass
                        : undefined
                    }>
                    <td
                      className={cn(
                        testOptTdClass,
                        testRunSpecsMethodCellClass,
                      )}>
                      <span className={testRunSpecsMethodNameClass}>{row.name}</span>
                      <span className={testOptTableMethodTypeClass}>{row.type}</span>
                      {row.inclusionBadge && (
                        <span
                          className={sourceBadgeClass(row.inclusionBadge.tone)}
                          title={
                            row.inclusionBadge.id === 'selected'
                              ? 'Method you selected when saving the spec'
                              : 'Caller or callee pulled in as a dependency'
                          }>
                          {row.inclusionBadge.label}
                        </span>
                      )}
                    </td>
                    <td className={testOptTdNumClass}>
                      {row.lineStart != null && row.lineEnd != null
                        ? `${row.lineStart}–${row.lineEnd}`
                        : '—'}
                    </td>
                    <td className={cn(testOptTdClass, testRunSpecsTcCellClass)}>
                      {row.testCases.length === 0 ? (
                        <span className={testRunSpecsNoTcClass}>No test mapped</span>
                      ) : (
                        <div className={testRunSpecsTcListClass}>
                          {row.testCases.map(tc => (
                            <TestCaseChip key={tc.testCaseId} tc={tc} />
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {failedUnmappedTestCases.length > 0 && (
        <div
          className={cn(
            testRunSpecsTablePanelClass,
            testRunSpecsTablePanelExtraClass,
          )}>
          <div className={testRunSpecsTableHeadClass}>
            <h3>Failed test cases (not mapped to methods)</h3>
            <p>
              These test cases were included from the failed-build checkbox but could not
              be linked to any code method, so they appear here instead of in the method
              table above.
            </p>
          </div>
          <div className={testRunSpecsExtraTcListClass}>
            {failedUnmappedTestCases.map(tc => (
              <TestCaseChip key={tc.testCaseId} tc={tc} />
            ))}
          </div>
        </div>
      )}

      {changedBuildTestCases.length > 0 && (
        <div
          className={cn(
            testRunSpecsTablePanelClass,
            testRunSpecsTablePanelExtraClass,
          )}>
          <div className={testRunSpecsTableHeadClass}>
            <h3>Changed build test cases</h3>
            <p>
              These test cases were added from modified scripts in the latest build. By
              design they are not linked to selected code methods, so they appear here
              instead of in the method table above.
            </p>
          </div>
          <div className={testRunSpecsExtraTcListClass}>
            {changedBuildTestCases.map(tc => (
              <TestCaseChip key={tc.testCaseId} tc={tc} />
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function StatBox({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'danger';
}) {
  return (
    <div className={testRunSpecsStatClass}>
      <div className={testRunSpecsStatValue(tone)}>{value}</div>
      <div className={testRunSpecsStatLabelClass}>{label}</div>
    </div>
  );
}

export default TestRunSpecsTab;
